// Command palette — Cmd+K / Ctrl+K global search + quick actions.
// Inspired by Linear, Raycast, and the legacy command palette.
// First iteration: navigation targets + "Nueva reserva" quick action. Later
// iterations add: recent bookings, recent players, cancelled bookings lookup,
// "Marcar acceso" direct flow.

import { useEffect, useMemo, useRef, useState } from 'react'
import { Search } from 'lucide-react'
import type { LucideIcon } from 'lucide-react'
import { Modal } from '@/shared/ui/modal'

export interface CommandAction {
  id: string
  label: string
  icon?: LucideIcon
  /** Optional group heading. Defaults to "Acciones". */
  group?: string
  /** Keywords fed into the fuzzy matcher. */
  keywords?: string[]
  /** Keyboard shortcut hint shown on the right. Cosmetic only. */
  hint?: string
  onSelect: () => void
}

export interface CommandPaletteProps {
  open: boolean
  onClose: () => void
  actions: CommandAction[]
}

function normalise(s: string): string {
  return s
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim()
}

function score(action: CommandAction, needle: string): number {
  if (!needle) return 1
  const hay = normalise([action.label, ...(action.keywords ?? [])].join(' '))
  const idx = hay.indexOf(needle)
  if (idx < 0) return 0
  // Prefer matches near the start of the label and shorter labels.
  const labelLen = action.label.length
  return 100 - idx + Math.max(0, 40 - labelLen)
}

export function CommandPalette({ open, onClose, actions }: CommandPaletteProps) {
  const [query, setQuery] = useState('')
  const [cursor, setCursor] = useState(0)
  const listRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (open) {
      setQuery('')
      setCursor(0)
    }
  }, [open])

  const matches = useMemo(() => {
    const needle = normalise(query)
    return actions
      .map((a) => ({ action: a, s: score(a, needle) }))
      .filter((x) => x.s > 0)
      .sort((a, b) => b.s - a.s)
      .slice(0, 20)
      .map((x) => x.action)
  }, [actions, query])

  const grouped = useMemo(() => {
    const m = new Map<string, CommandAction[]>()
    for (const a of matches) {
      const g = a.group ?? 'Acciones'
      const list = m.get(g) ?? []
      list.push(a)
      m.set(g, list)
    }
    return Array.from(m.entries())
  }, [matches])

  // Keep cursor in range when matches change.
  useEffect(() => {
    setCursor((c) => (matches.length === 0 ? 0 : Math.min(c, matches.length - 1)))
  }, [matches.length])

  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'ArrowDown') {
        e.preventDefault()
        setCursor((c) => (matches.length === 0 ? 0 : (c + 1) % matches.length))
      } else if (e.key === 'ArrowUp') {
        e.preventDefault()
        setCursor((c) => (matches.length === 0 ? 0 : (c - 1 + matches.length) % matches.length))
      } else if (e.key === 'Enter') {
        e.preventDefault()
        const target = matches[cursor]
        if (target) {
          target.onSelect()
          onClose()
        }
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open, matches, cursor, onClose])

  if (!open) return null

  return (
    <Modal title="Búsqueda rápida" onClose={onClose} size="md" showCloseButton={false}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            padding: '8px 10px',
            border: '2px solid var(--line)',
            borderRadius: 6,
            background: 'var(--panel-2)',
          }}
        >
          <Search size={14} />
          <input
            data-autofocus="true"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Buscar o ejecutar una acción…"
            aria-label="Búsqueda rápida"
            style={{
              flex: 1,
              background: 'transparent',
              border: 'none',
              color: 'var(--fg)',
              fontSize: 'var(--fs-md)',
              outline: 'none',
            }}
          />
          <span className="faint mono" style={{ fontSize: 'var(--fs-2xs)' }}>
            ↵ seleccionar · Esc cerrar
          </span>
        </div>

        <div
          ref={listRef}
          role="listbox"
          aria-label="Resultados"
          style={{ maxHeight: 360, overflowY: 'auto' }}
        >
          {matches.length === 0 && (
            <div className="faint" style={{ padding: '20px 8px', textAlign: 'center', fontSize: 'var(--fs-md)' }}>
              Sin resultados para "{query}"
            </div>
          )}
          {grouped.map(([group, list]) => (
            <div key={group}>
              <div
                className="caps"
                style={{
                  color: 'var(--fg-faint)',
                  fontSize: 'var(--fs-2xs)',
                  padding: '8px 10px 4px',
                  letterSpacing: 'var(--ls-caps)',
                }}
              >
                {group}
              </div>
              {list.map((action) => {
                const idx = matches.indexOf(action)
                const active = idx === cursor
                return (
                  <button
                    key={action.id}
                    type="button"
                    role="option"
                    aria-selected={active}
                    onMouseEnter={() => setCursor(idx)}
                    onClick={() => {
                      action.onSelect()
                      onClose()
                    }}
                    style={{
                      all: 'unset',
                      cursor: 'pointer',
                      display: 'grid',
                      gridTemplateColumns: '20px 1fr auto',
                      alignItems: 'center',
                      gap: 10,
                      padding: '10px 12px',
                      borderRadius: 6,
                      width: '100%',
                      boxSizing: 'border-box',
                      background: active
                        ? 'color-mix(in oklch, var(--accent) 14%, transparent)'
                        : 'transparent',
                      color: active ? 'var(--accent)' : 'var(--fg)',
                    }}
                  >
                    {action.icon ? (() => { const ActionIcon = action.icon!; return <ActionIcon size={13} /> })() : (
                      <span style={{ width: 13 }} />
                    )}
                    <span style={{ fontSize: 'var(--fs-md)' }}>{action.label}</span>
                    {action.hint && (
                      <span className="faint mono" style={{ fontSize: 'var(--fs-2xs)' }}>
                        {action.hint}
                      </span>
                    )}
                  </button>
                )
              })}
            </div>
          ))}
        </div>
      </div>
    </Modal>
  )
}

/** Global listener — call once at the app shell level with the setter. */
export function useCommandPaletteShortcut(setOpen: (v: boolean) => void) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault()
        setOpen(true)
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [setOpen])
}
